import { ExternalLink } from "lucide-react";

interface Project {
  name: string;
  oneLiner: string;
  tech: string[];
  highlight: string;
  link: { url: string; label: string } | null;
  image: { src: string; bg: string; imgClass: string };
  comingSoon?: boolean;
}

const projects: Project[] = [
  {
    name: "SmartDreams",
    oneLiner: "AI-powered children's reading app that encourages creativity and literacy. Recognized by Apple as a Top AI App of 2023.",
    tech: ["Swift (iOS)", "Python", "OpenAI", "GCP", "AWS S3"],
    highlight: "Apple Top AI App 2023 · 4.9★ App Store",
    link: null,
    image: {
      src: "/smartdreams-logo.svg",
      bg: "#021948",
      imgClass: "w-2/3 object-contain",
    },
  },
  {
    name: "Heritage Clubhouse",
    oneLiner: "A web-based golf passport — a personal, map-forward record of the courses you've played, upcoming rounds, and bucket list, with an opt-in social layer to share your map and overlay friends' maps onto your own.",
    tech: ["Next.js", "TypeScript", "Tailwind", "Leaflet", "Supabase", "Resend", "Vercel"],
    highlight: "Interactive course map · magic-link auth · public share pages with OG cards",
    link: { url: "https://heritageclubhouse.app", label: "heritageclubhouse.app" },
    image: {
      src: "/heritage-clubhouse.svg",
      bg: "#f3ecda",
      imgClass: "w-2/3 object-contain",
    },
  },
  {
    name: "PointsForecast",
    oneLiner: "Predicts upcoming credit card transfer bonuses using historical data analysis. 227+ validated records across 4 issuers and 28 airline partners.",
    tech: ["Next.js 14", "Tailwind CSS", "Supabase", "Edge Functions", "Resend", "Vercel"],
    highlight: "Bayesian prediction engine · Seasonality & magnitude scoring",
    link: { url: "https://pointsforecast.com", label: "pointsforecast.com" },
    image: {
      src: "/pointsforecast-icon.svg",
      bg: "#1a1005",
      imgClass: "w-2/3 object-contain",
    },
  },
  {
    name: "DiscStash",
    oneLiner: "A web app for DVD, Blu-ray, and 4K UHD collectors to catalog, track, and value their physical media libraries.",
    tech: ["React", "TypeScript", "Supabase", "Stripe", "TMDb API", "Lovable", "Replit", "Claude"],
    highlight: "Full-stack app with third-party integrations",
    link: { url: "https://discstash.com", label: "discstash.com" },
    image: {
      src: "/discstash-icon.svg",
      bg: "#1a0533",
      imgClass: "w-1/2 object-contain",
    },
  },
  {
    name: "Steady News",
    oneLiner: "A daily news briefing app that uses AI to transform anxiety-inducing headlines into calm, digestible summaries.",
    tech: ["React", "TypeScript", "Express", "Supabase", "Stripe", "OpenAI", "NewsAPI", "Resend", "Replit"],
    highlight: "AI summarization, text-to-speech, and calmness scoring",
    link: { url: "https://steadynews.app", label: "steadynews.app" },
    image: {
      src: "/barn-owl.png",
      bg: "#f0ece0",
      imgClass: "w-1/2 object-contain",
    },
  },
  {
    name: "HYROX World Orbit",
    oneLiner: "An interactive 3D globe for discovering HYROX fitness race locations, schedules, and event details worldwide.",
    tech: ["React", "TypeScript", "Supabase", "Framer Motion", "Recharts", "Lovable"],
    highlight: "3D globe with race filtering, calendar, and passport views",
    link: null,
    comingSoon: true,
    image: {
      src: "/hyrox-globe.svg",
      bg: "#0a1628",
      imgClass: "w-2/3 object-contain",
    },
  },
  {
    name: "Pulse",
    oneLiner: "A personal health intelligence platform connecting wearables, body composition, genetics, and habits into AI-powered recommendations.",
    tech: ["Next.js", "React", "Supabase", "Claude API", "Python", "Vercel"],
    highlight: "AI-powered weekly health briefs and natural language queries",
    link: null,
    comingSoon: true,
    image: {
      src: "/pulse-icon.svg",
      bg: "#0f0a1e",
      imgClass: "w-2/3 object-contain",
    },
  },
  {
    name: "Clean Energy Scoreboard",
    oneLiner: "ESPN for the energy transition — global scoreboards, country rankings, milestone countdowns, and bar chart races tracking clean energy's progress.",
    tech: ["Next.js 15", "Tailwind CSS", "Supabase", "Recharts", "D3", "Vercel", "@vercel/og"],
    highlight: "210+ countries · 50 US states · Live data from Ember & EIA",
    link: null,
    comingSoon: true,
    image: {
      src: "/clean-energy-icon.svg",
      bg: "#071a0e",
      imgClass: "w-2/3 object-contain",
    },
  },
];

const BuiltSection = () => {
  return (
    <section id="built" className="section-padding bg-background">
      <div className="content-max">
        <h2 className="fade-up font-headline text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight mb-3">
          What I've Built
        </h2>
        <p className="fade-up font-body text-muted-foreground text-lg md:text-xl mb-12 md:mb-16" style={{ transitionDelay: "100ms" }}>
          Side projects and personal apps I've designed, built, and shipped independently using AI tools.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.map((project, i) => (
            <div
              key={project.name}
              className="fade-up group bg-[hsl(240_5%_15%)] border border-[hsl(240_4%_18%)] rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/40 hover:shadow-[0_0_24px_-6px_hsl(var(--primary)/0.15)] hover:-translate-y-1 flex flex-col"
              style={{ transitionDelay: `${200 + i * 75}ms` }}
            >
              <div
                className="aspect-video flex items-center justify-center overflow-hidden relative"
                style={{ backgroundColor: project.image.bg }}
              >
                <img
                  src={project.image.src}
                  alt={project.name}
                  className={project.image.imgClass}
                />
                {project.comingSoon && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 font-mono text-[10px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/25 backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    In Development
                  </span>
                )}
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-body text-base font-semibold text-foreground mb-1.5">
                  {project.name}
                </h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4">
                  {project.oneLiner}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {project.tech.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center font-body text-[11px] px-2.5 py-1 rounded-md bg-[hsl(240_5%_17%)] text-foreground/80"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <p className="font-mono text-xs text-primary mb-4 mt-auto">
                  {project.highlight}
                </p>

                {project.link && (
                  <a
                    href={project.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors font-body"
                  >
                    Visit <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BuiltSection;
